import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { ChannelItem } from '@components/AllChannelsList/AllChannelsList'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { Avatar } from '@components/Avatar/Avatar'
import {
    DirectMessageIcon,
    DirectMessageName,
} from '@components/DirectMessages/DirectMessageListItem'
import {
    useLiveClient,
    useLiveSpaceSummary,
    useLiveUserDeviceState,
} from '@components/Live/LiveClientProvider'
import { NavItem } from '@components/NavItem/_NavItem'
import { RenewMembershipItem } from '@components/SideBars/SpaceSideBar/RenewMembershipItem'
import { SoundWaveIcon } from '@components/SideBars/SpaceSideBar/SoundWaveIcon'
import * as soundWaveStyles from '@components/SideBars/SpaceSideBar/SoundWaveIcon.css'
import { StakingBadge } from '@components/StakingBadge/StakingBadge'
import { TouchScrollToTopScrollId } from '@components/TouchTabBar/TouchScrollToTopScrollId'
import { genId, makeDefaultChannelStreamId, Membership } from '@towns-protocol/sdk'
import {
    Badge,
    Box,
    BoxProps,
    Divider,
    Icon,
    IconButton,
    IconName,
    IconProps,
    MotionBox,
    Paragraph,
    Stack,
    Text,
    TextField,
} from '@ui'
import fuzzysort from 'fuzzysort'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { useChannelEntitlements } from 'hooks/useChannelEntitlements'
import { useCreateLink } from 'hooks/useCreateLink'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useReviews } from 'hooks/useReviews'
import { DMChannelMenuItem, MixedChannelMenuItem, useSortedChannels } from 'hooks/useSortedChannels'
import { useUnseenChannelIds } from 'hooks/useUnseenChannelIdsCount'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useReviewStore } from 'store/reviewStore'
import { useStore } from 'store/store'
import { atoms } from 'ui/styles/atoms.css'
import { vars } from 'ui/styles/vars.css'
import { addressFromSpaceId, notUndefined } from 'ui/utils/utils'
import {
    ChannelContextProvider,
    DMChannelContextUserLookupProvider,
    NULL_ADDRESS,
    Permission,
    SpaceData,
    TownsStreamMember,
    useConnectivity,
    useDMLatestMessage,
    useHasPermission,
    useIsSpaceOwner,
    useMyMemberships,
    useSpaceData,
    useSpaceDelegationOperator,
    useSpaceMembers,
    useSpaceThreadRootsUnreadCount,
    useSpaceUnreadThreadMentions,
    useUserLookupContext,
} from 'use-towns-client'
import { env } from 'utils'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

const transition = {
    layout: { type: 'spring' as const, duration: 0.4 },
}

const variants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
}

export const ChannelSection = (props: {
    isSearching: boolean
    onToggleSearch: (isSearching: boolean) => void
}) => {
    const { isSearching, onToggleSearch } = props
    const space = useSpaceData()
    const { loggedInWalletAddress } = useConnectivity()

    const [searchString, setSearchString] = useState<string>('')
    const [caretVisible, setCaretVisible] = useState<boolean>(false)
    const { memberIds } = useSpaceMembers()
    const { lookupUser } = useUserLookupContext()
    const { openPanel } = usePanelActions()

    const members = useMemo(() => {
        return memberIds.map((userId) => lookupUser(userId)).filter(notUndefined)
    }, [lookupUser, memberIds])

    const { unseenChannelIds } = useUnseenChannelIds()

    const { hasPermission: canCreateChannel } = useHasPermission({
        spaceId: space?.id,
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.AddRemoveChannels,
    })

    const onFocus = useCallback(() => {
        onToggleSearch(true)
        const timeout = setTimeout(() => {
            setCaretVisible(true)
        }, 500)
        return () => {
            clearTimeout(timeout)
        }
    }, [onToggleSearch, setCaretVisible])

    const onCloseSearch = useCallback(() => {
        onToggleSearch(false)
        setCaretVisible(false)
        setSearchString('')
    }, [onToggleSearch, setCaretVisible])

    const onChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchString(event.target.value)
        },
        [setSearchString],
    )

    const onScroll = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur()
        }
    }

    const {
        sortedUnreadChannels: unreadChannels,
        sortedSpaceChannels: readChannels,
        sortedDmChannels: readDms,
        unjoinedChannels,
        sortedFavoriteChannels: favoriteChannels,
    } = useSortedChannels({
        spaceId: space?.id ?? '',
    })

    const {
        filteredUnreadChannels,
        filteredSpaceChannels,
        filteredDmChannels,
        filteredFavoriteChannels,
    } = useMemo(() => {
        return {
            filteredUnreadChannels: fuzzysort
                .go(searchString, unreadChannels, { keys: ['label', 'search'], all: true })
                .map((m) => m.obj),
            filteredSpaceChannels: fuzzysort
                .go(
                    searchString,
                    [...readChannels, ...(searchString ? unjoinedChannels : [])].filter(
                        notUndefined,
                    ),
                    {
                        keys: ['label', 'search'],
                        all: true,
                    },
                )
                .map((m) => m.obj),
            filteredDmChannels: fuzzysort
                .go(searchString, readDms, { keys: ['label', 'search'], all: true })
                .map((m) => m.obj),
            filteredFavoriteChannels: fuzzysort
                .go(searchString, favoriteChannels, { keys: ['label', 'search'], all: true })
                .map((m) => m.obj),
        }
    }, [readChannels, readDms, searchString, unjoinedChannels, unreadChannels, favoriteChannels])

    const filteredMembers = useMemo(() => {
        if (searchString.length === 0) {
            return []
        }
        return fuzzysort
            .go(searchString, members, { keys: ['username', 'displayName'], limit: 10 })
            .map((m) => m.obj)
    }, [members, searchString])

    const onShowBrowseChannels = useCallback(() => {
        Analytics.getInstance().track(
            'clicked browse channels',
            {
                spaceId: space?.id,
            },
            () => {
                console.log('clicked browse channels', { spaceId: space?.id })
            },
        )
        openPanel(CHANNEL_INFO_PARAMS.BROWSE_CHANNELS)
    }, [openPanel, space?.id])

    const { createLink } = useCreateLink()
    const threadsLink = createLink({ route: 'threads' })
    const mentionsLink = createLink({ route: 'mentions' })
    const unreadThreadsCount = useSpaceThreadRootsUnreadCount()
    const unreadThreadMentions = useSpaceUnreadThreadMentions(space?.id)

    const filteredMenuItems = useMemo(
        () =>
            fuzzysort.go(
                searchString,
                [
                    { name: 'threads', value: 'threads' },
                    { name: 'mentions', value: 'mentions' },
                ],
                { key: 'name', all: true },
            ),
        [searchString],
    )

    const displayThreadsItem =
        threadsLink && (!isSearching || filteredMenuItems.some((f) => f.obj.value === 'threads'))

    const displayMentionsItem =
        mentionsLink && (!isSearching || filteredMenuItems.some((f) => f.obj.value === 'mentions'))

    const openCreateChannelPanel = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.CREATE_CHANNEL, {
            channelFormId: `formid-${genId(5)}`,
        })
    }, [openPanel])

    const hasResult = filteredSpaceChannels.length > 0 || filteredUnreadChannels.length > 0

    const scrollRef = useRef<HTMLDivElement>(null)

    const { data: myAbstractAccountAddress } = useMyAbstractAccountAddress()

    useEffect(() => {
        if (space?.id && scrollRef.current) {
            scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }, [space?.id])

    if (!space) {
        return null
    }

    return (
        <Stack grow position="relative">
            <Box absoluteFill>
                {/* Search Bar */}
                <Stack
                    horizontal
                    paddingX="md"
                    alignItems="center"
                    paddingY="md"
                    gap="sm"
                    style={{
                        caretColor: caretVisible ? vars.color.foreground.accent : 'rgba(0,0,0,0)',
                    }}
                >
                    <TextField
                        grow
                        placeholder={`Search ${space.name}...`}
                        height="x5"
                        background="level2"
                        value={searchString}
                        onFocus={onFocus}
                        onChange={onChange}
                    />
                    {isSearching && <IconButton icon="close" onClick={onCloseSearch} />}
                </Stack>

                {/* Content */}
                <MotionBox
                    scroll
                    grow
                    scrollbars
                    initial="initial"
                    animate="animate"
                    variants={variants}
                    transition={transition}
                    id={TouchScrollToTopScrollId.HomeTabScrollId}
                    background="level2"
                    borderTopLeftRadius="md"
                    borderTopRightRadius="md"
                    ref={scrollRef}
                    onScroll={onScroll}
                >
                    {/* channel card */}
                    <Box paddingTop="sm" color="gray2">
                        {/* Threads and Mentions */}

                        {/* Affordances */}
                        <TouchRenewMembershipWrapper />
                        <TouchReviewTownWrapper
                            myAbstractAccountAddress={myAbstractAccountAddress}
                        />
                        <TouchDelegateToNodeWrapper
                            myAbstractAccountAddress={myAbstractAccountAddress}
                        />

                        {displayThreadsItem && (
                            <TouchGenericResultRow
                                to={threadsLink}
                                title="Threads"
                                icon="message"
                                highlight={unreadThreadsCount > 0}
                                badgeCount={unreadThreadMentions}
                            />
                        )}
                        {displayMentionsItem && (
                            <TouchGenericResultRow to={mentionsLink} title="Mentions" icon="at" />
                        )}

                        {/* Live Channels */}
                        <TouchLiveChannelSection space={space} />

                        {/* Channel Lists */}
                        <ChannelList
                            label="Unread"
                            channelItems={filteredUnreadChannels}
                            space={space}
                        />
                        <ChannelList
                            label="Favorites"
                            channelItems={filteredFavoriteChannels}
                            space={space}
                        />
                        <ChannelList
                            label="Channels"
                            channelItems={filteredSpaceChannels}
                            space={space}
                        />
                        <BrowseChannelRow
                            badgeCount={unseenChannelIds.size}
                            onClick={onShowBrowseChannels}
                        />
                        {canCreateChannel && <CreateChannelRow onClick={openCreateChannelPanel} />}
                        {filteredDmChannels.length > 0 && (
                            <ChannelList
                                label="Direct Messages"
                                channelItems={readDms}
                                space={space}
                            />
                        )}

                        {/* Members */}
                        {filteredMembers.length > 0 && (
                            <>
                                <Spacer />
                                <SectionHeader title="Members" />
                                <UserList members={filteredMembers} />
                            </>
                        )}

                        {/* Search for term */}
                        {isSearching && searchString.length > 1 && (
                            <>
                                {hasResult ? <Divider space="sm" /> : <></>}
                                <SearchForTermRow searchString={searchString} />
                            </>
                        )}
                    </Box>
                </MotionBox>
            </Box>
        </Stack>
    )
}

// Supporting components (moved from archived TouchHome.tsx)
const ChannelList = React.memo(
    (props: { label?: string; channelItems: MixedChannelMenuItem[]; space: SpaceData }) => {
        const { channelItems, label, space } = props
        const myMemberships = useMyMemberships()

        if (channelItems.length === 0) {
            return <></>
        }

        const items = channelItems.map((c) =>
            c.type === 'dm' ? (
                <DirectMessageItem key={c.id} dm={c} unread={c.unread} />
            ) : c.joined ? (
                <TouchChannelResultRow
                    key={c.id}
                    channelId={c.id}
                    spaceId={space.id}
                    itemLink={{ channelId: c.id }}
                    name={c.channel.label}
                    unread={c.unread}
                    mentionCount={c.mentionCount}
                    muted={c.muted}
                />
            ) : (
                <Box paddingY="sm" paddingX="md" key={c.id}>
                    <ChannelContextProvider channelId={c.id}>
                        <ChannelItem
                            key={c.id}
                            space={space}
                            channelNetworkId={c.id}
                            name={c.channel.label}
                            isJoined={myMemberships[c.id] === Membership.Join}
                        />
                    </ChannelContextProvider>
                </Box>
            ),
        )

        return (
            <>
                {label && <SectionHeader title={label} />}
                {items}
            </>
        )
    },
)

const DirectMessageItem = (props: { dm: DMChannelMenuItem; unread: boolean }) => {
    const { dm, unread } = props
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const { unreadCount } = useDMLatestMessage(dm.id)
    return (
        <DMChannelContextUserLookupProvider
            key={dm.id}
            channelId={dm.id}
            abstractAccountAddress={abstractAccountAddress}
        >
            <TouchChannelResultRow
                key={dm.id}
                itemLink={{ messageId: dm.id }}
                name={
                    <DirectMessageName
                        channelId={dm.channel.id}
                        label={dm.channel.properties?.name}
                    />
                }
                unread={unread}
                mentionCount={unreadCount}
                muted={false}
                icontElement={
                    <Box width="x4">
                        <DirectMessageIcon channel={dm.channel} width="x4" />
                    </Box>
                }
            />
        </DMChannelContextUserLookupProvider>
    )
}

export const TouchChannelResultRow = (props: {
    itemLink: { channelId: string } | { messageId: string }
    name: React.ReactNode
    unread: boolean
    mentionCount: number
    muted: boolean
    icontElement?: React.ReactNode
    channelId?: string
    spaceId?: string
    onClick?: () => void
}) => {
    const { itemLink, name, unread, mentionCount, muted, channelId, spaceId, onClick } = props
    const { createLink } = useCreateLink()
    const link =
        createLink(
            'channelId' in itemLink
                ? { channelId: itemLink.channelId }
                : { messageId: itemLink.messageId },
        ) + `?ref=home`

    const { hasSomeEntitlement } = useChannelEntitlements({
        spaceId,
        channelId,
    })

    return (
        <NavItem to={link} padding="none" onClick={onClick}>
            <NavItemContent
                key={link}
                fontWeight={unread ? 'strong' : 'normal'}
                color={unread ? 'default' : undefined}
            >
                {props.icontElement ?? (
                    <SearchResultItemIcon type={hasSomeEntitlement ? 'lock' : 'tag'} />
                )}
                <Text truncate textAlign="left">
                    {name}
                </Text>
                <Box grow />
                {muted && <Icon type="muteActive" color="gray2" size="square_xs" />}
                {mentionCount > 0 && <Badge value={mentionCount}>{mentionCount}</Badge>}
            </NavItemContent>
        </NavItem>
    )
}

export const TouchSpaceResultRow = (props: {
    itemLink: { spaceId: string }
    name: React.ReactNode
    icontElement?: React.ReactNode
    onClick?: () => void
}) => {
    const { itemLink, name, onClick } = props
    const { createLink } = useCreateLink()
    const link = createLink({ spaceId: itemLink.spaceId, channelId: undefined }) + `?ref=home`

    return (
        <NavItem to={link} padding="none" onClick={onClick}>
            <NavItemContent key={link} fontWeight="normal" color="gray1">
                {props.icontElement ?? <SearchResultItemIcon type="tag" />}
                <Text truncate textAlign="left">
                    {name}
                </Text>
                <Box grow />
            </NavItemContent>
        </NavItem>
    )
}

const UserList = (props: { members: TownsStreamMember[] }) => {
    const { members } = props

    return (
        <Stack>
            {members.map((m) => (
                <TouchUserResultRow key={m.userId} member={m} />
            ))}
        </Stack>
    )
}

export const TouchUserResultRow = (props: { member: TownsStreamMember; onClick?: () => void }) => {
    const { member, onClick } = props
    const { createLink } = useCreateLink()
    const link = createLink({ profileId: member.userId })

    return (
        <NavItem to={link} height="x6" padding="none" onClick={onClick}>
            <NavItemContent key={member.userId}>
                <Stack horizontal centerContent gap="sm">
                    <Avatar lazy size="avatar_x4" userId={member.userId} />
                    <Text truncate color="default">
                        {getPrettyDisplayName(member)}
                    </Text>
                </Stack>
                <Box grow />
            </NavItemContent>
        </NavItem>
    )
}

const SectionHeader = (props: { title: string; children?: React.ReactNode }) => {
    const { title } = props
    return (
        <Box horizontal paddingX="md" paddingTop="md" paddingBottom="sm" alignItems="center">
            <Text color="gray2">{title}</Text>
            {props.children ? (
                <Box grow horizontal justifyContent="end">
                    {props.children}
                </Box>
            ) : (
                <></>
            )}
        </Box>
    )
}

const SearchForTermRow = (props: { searchString: string }) => {
    const { searchString } = props
    const navigate = useNavigate()
    const { createLink } = useCreateLink()
    const setSearchTerms = useStore(({ setSearchTerms }) => setSearchTerms)

    const onSearchTerm = useCallback(() => {
        const link = createLink({ route: 'search' })
        setSearchTerms(searchString)
        if (link) {
            navigate(link)
        }
    }, [createLink, navigate, searchString, setSearchTerms])

    return (
        <Box paddingX="md" paddingY="sm">
            <Box horizontal gap="sm" alignItems="center">
                <SearchResultItemIcon type="search" />
                <Stack horizontal grow overflow="hidden" paddingY="sm" onClick={onSearchTerm}>
                    <Paragraph truncate color="gray1">
                        Search messages for &quot;
                        <span className={atoms({ fontWeight: 'medium', color: 'default' })}>
                            {searchString}
                        </span>
                        &quot;
                    </Paragraph>
                </Stack>
            </Box>
        </Box>
    )
}

// Wrapper components for affordances
const TouchRenewMembershipWrapper = () => {
    return <RenewMembershipItem />
}

const TouchReviewTownWrapper = (props: { myAbstractAccountAddress: `0x${string}` | undefined }) => {
    const { myAbstractAccountAddress } = props
    const space = useSpaceData()
    const { openPanel } = usePanelActions()
    const { dismissReview, hasReviewBeenDismissed } = useReviewStore()
    const { reviews } = useReviews(space?.id ?? '')
    const spaceDetails = getCachedSpaceDataForAnalytics()

    const hasUserReviewed = myAbstractAccountAddress
        ? reviews.some((review) => review.author === myAbstractAccountAddress)
        : false

    const shouldShowReviewPrompt =
        space?.id &&
        !hasReviewBeenDismissed(space.id) &&
        !hasUserReviewed &&
        env.VITE_REVIEWS_ENABLED

    const handleReviewClick = useEvent(() => {
        if (space?.id) {
            Analytics.getInstance().track('clicked review prompt', {
                action: 'review',
                spaceName: space.name,
                spaceId: space.id,
                gatedSpace: spaceDetails.gatedSpace,
                gatedChannel: spaceDetails.gatedChannel,
                pricingModule: spaceDetails.pricingModule,
            })
        }
        openPanel(CHANNEL_INFO_PARAMS.REVIEWS)
    })

    const handleReviewDismiss = useEvent(() => {
        if (space?.id) {
            Analytics.getInstance().track('clicked review prompt', {
                action: 'dismiss',
                spaceName: space.name,
                spaceId: space.id,
                gatedSpace: spaceDetails.gatedSpace,
                gatedChannel: spaceDetails.gatedChannel,
                pricingModule: spaceDetails.pricingModule,
            })
            dismissReview(space.id)
        }
    })

    if (!shouldShowReviewPrompt) {
        return null
    }

    return (
        <TouchActionRow
            title="Review This Town"
            icon="star"
            iconColor="blue"
            titleColor="blue"
            onClick={handleReviewClick}
            onDismiss={handleReviewDismiss}
        />
    )
}

const TouchDelegateToNodeWrapper = (props: {
    myAbstractAccountAddress: `0x${string}` | undefined
}) => {
    const { myAbstractAccountAddress } = props
    const space = useSpaceData()
    const { openPanel } = usePanelActions()
    const spaceAddress = addressFromSpaceId(space?.id ?? '')
    const { isOwner: isSpaceOwner } = useIsSpaceOwner(spaceAddress, myAbstractAccountAddress)
    const { enableDelegation } = useFeatureFlags()
    const { isSuccess: isOperatorSuccess, data: nodeOperator } =
        useSpaceDelegationOperator(spaceAddress)

    const canDelegateToNode =
        enableDelegation &&
        isSpaceOwner &&
        (isOperatorSuccess ? nodeOperator === NULL_ADDRESS : false)

    const [isDelegateToNodeVisible, setIsDelegateToNodeVisible] = useState(canDelegateToNode)

    // Update delegate visibility when canDelegateToNode changes
    React.useEffect(() => {
        setIsDelegateToNodeVisible(canDelegateToNode)
    }, [canDelegateToNode])

    const handleDelegateToNodeClick = useEvent(() => {
        if (space?.id) {
            Analytics.getInstance().track('clicked delegation prompt', {
                spaceName: space.name,
                spaceId: space.id,
            })
        }
        openPanel(CHANNEL_INFO_PARAMS.DELEGATE_TO_NODE_OPERATOR)
    })

    const handleDelegateToNodeDismiss = useEvent(() => {
        setIsDelegateToNodeVisible(false)
    })

    if (!isDelegateToNodeVisible) {
        return null
    }

    return (
        <TouchActionRow
            title="Delegate to a Node"
            iconElement={
                <Box centerContent square="square_lg" background="level2" rounded="sm" padding="sm">
                    <StakingBadge size="x2.5" type="diamond" />
                </Box>
            }
            titleColor="blue"
            onClick={handleDelegateToNodeClick}
            onDismiss={handleDelegateToNodeDismiss}
        />
    )
}

const TouchActionRow = (props: {
    title: string
    icon?: IconName
    iconElement?: React.ReactNode
    iconColor?: 'blue' | 'gray2' | 'default'
    titleColor?: 'blue' | 'gray2' | 'default'
    onClick: () => void
    onDismiss: () => void
}) => {
    const { title, icon, iconElement, iconColor, titleColor, onClick, onDismiss } = props

    return (
        <NavItem padding="none" onClick={onClick}>
            <NavItemContent>
                {iconElement || <SearchResultItemIcon type={icon || 'star'} color={iconColor} />}
                <Text color={titleColor}>{title}</Text>
                <Box grow />
                <IconButton
                    icon="close"
                    size="square_sm"
                    onClick={(e) => {
                        e.stopPropagation()
                        onDismiss()
                    }}
                />
            </NavItemContent>
        </NavItem>
    )
}

const SearchResultItemIcon = ({ type, ...boxProps }: { type: IconProps['type'] } & BoxProps) => (
    <Box
        centerContent
        shrink={false}
        square="square_lg"
        background="level3"
        rounded="sm"
        color="inherit"
        {...boxProps}
    >
        <Icon type={type} size="square_sm" />
    </Box>
)

export const TouchGenericResultRow = (props: {
    to: string
    title: string
    icon: IconName
    highlight?: boolean
    badgeCount?: number
}) => {
    return (
        <NavItem to={props.to} padding="none">
            <NavItemContent>
                <SearchResultItemIcon type={props.icon} color="gray2" />
                <Stack gap="sm" overflow="hidden" paddingY="xxs">
                    <Text
                        truncate
                        fontWeight={props.highlight ? 'strong' : undefined}
                        color={props.highlight ? 'default' : undefined}
                    >
                        {props.title}
                    </Text>
                </Stack>
                <Box grow />
                {(props?.badgeCount ?? 0) > 0 && (
                    <Badge value={props.badgeCount}>{props.badgeCount}</Badge>
                )}
            </NavItemContent>
        </NavItem>
    )
}

const CreateChannelRow = (props: { onClick: () => void }) => {
    return (
        <NavItem padding="none" onClick={props.onClick}>
            <NavItemContent>
                <SearchResultItemIcon type="plus" />
                <Text>Create channel</Text>
            </NavItemContent>
        </NavItem>
    )
}

const BrowseChannelRow = (props: { onClick: () => void; badgeCount?: number }) => {
    return (
        <NavItem padding="none" onClick={props.onClick}>
            <NavItemContent>
                <SearchResultItemIcon type="search" />
                <Text>Browse channels</Text>
                {props.badgeCount && props.badgeCount > 0 ? (
                    <Badge value={props.badgeCount} />
                ) : (
                    <></>
                )}
            </NavItemContent>
        </NavItem>
    )
}

const NavItemContent = (props: BoxProps) => (
    <Stack
        horizontal
        paddingX="sm"
        alignItems="center"
        width="100%"
        gap="sm"
        overflowX="hidden"
        overflowY="hidden"
        {...props}
    />
)

const Spacer = () => <Box minHeight="x2" width="x2" />

/** Format count to compact display (e.g., 999, 1.2k, 10k) */
function formatLiveCount(count: number): string {
    if (count < 1000) {
        return String(count)
    }
    if (count < 10000) {
        return `${(count / 1000).toFixed(1)}k`
    }
    return `${Math.floor(count / 1000)}k`
}

type LiveChannelInfo = {
    channelId: string
    channelLabel: string
    stageCount: number
    listenerCount: number
    isUserLive: boolean
}

/**
 * Live channel section for mobile - shows channels with active live sessions
 */
export const TouchLiveChannelSection = React.memo(function TouchLiveChannelSection(props: {
    space: SpaceData
}) {
    const { space } = props
    const deviceState = useLiveUserDeviceState()
    const spaceSummary = useLiveSpaceSummary(space.id)

    // Get the default channel ID
    const defaultChannelId = useMemo(() => {
        const spaceAddress = addressFromSpaceId(space.id)
        if (!spaceAddress) {
            return undefined
        }
        return makeDefaultChannelStreamId(spaceAddress)
    }, [space.id])

    // Check which channel the user is currently live in (if any)
    const userLiveChannelId = useMemo(() => {
        if (deviceState?.type === 'live' && deviceState.spaceId === space.id) {
            return deviceState.channelId
        }
        return undefined
    }, [deviceState, space.id])

    // Build list of live channels
    const liveChannels = useMemo((): LiveChannelInfo[] => {
        if (!spaceSummary?.channels || !defaultChannelId) {
            return []
        }

        const channels: LiveChannelInfo[] = []
        const allChannels = space.channelGroups?.flatMap((g) => g.channels) ?? []

        // Always include default channel (general) so users can start a live session
        const defaultChannel = allChannels.find((c) => c.id === defaultChannelId)
        const defaultChannelState = spaceSummary.channels[defaultChannelId]

        if (defaultChannel) {
            channels.push({
                channelId: defaultChannelId,
                channelLabel: defaultChannel.label,
                stageCount: defaultChannelState?.stage.totalCount ?? 0,
                listenerCount: defaultChannelState?.listeners.totalCount ?? 0,
                isUserLive: userLiveChannelId === defaultChannelId,
            })
        }

        // Add other channels that have activity
        for (const [channelId, channelState] of Object.entries(spaceSummary.channels)) {
            if (channelId === defaultChannelId) {
                continue
            }

            const hasActivity =
                channelState.stage.totalCount > 0 || channelState.listeners.totalCount > 0

            if (hasActivity) {
                const channel = allChannels.find((c) => c.id === channelId)
                if (channel) {
                    channels.push({
                        channelId,
                        channelLabel: channel.label,
                        stageCount: channelState.stage.totalCount,
                        listenerCount: channelState.listeners.totalCount,
                        isUserLive: userLiveChannelId === channelId,
                    })
                }
            }
        }

        return channels
    }, [spaceSummary?.channels, space.channelGroups, defaultChannelId, userLiveChannelId])

    // Only show if deviceState exists (user is connected to live)
    if (!deviceState) {
        return null
    }

    // Don't show section if no channels to display
    if (liveChannels.length === 0) {
        return null
    }

    return (
        <>
            <SectionHeader title="Live" />
            {liveChannels.map((channel) => (
                <TouchLiveChannelItem
                    key={`live-${channel.channelId}`}
                    spaceId={space.id}
                    channelId={channel.channelId}
                    channelLabel={channel.channelLabel}
                    stageCount={channel.stageCount}
                    listenerCount={channel.listenerCount}
                    isUserLive={channel.isUserLive}
                />
            ))}
        </>
    )
})

const TouchLiveChannelItem = React.memo(function TouchLiveChannelItem(props: {
    spaceId: string
    channelId: string
    channelLabel: string
    stageCount: number
    listenerCount: number
    isUserLive: boolean
}) {
    const { spaceId, channelId, channelLabel, stageCount, listenerCount, isUserLive } = props
    const { joinChannel } = useLiveClient()
    const { openPanel } = usePanelActions()

    const onClick = useCallback(() => {
        joinChannel(spaceId, channelId)
        openPanel(CHANNEL_INFO_PARAMS.LIVE_PARTICIPANTS, {
            spaceId,
            channelId,
            channelName: channelLabel,
        })
    }, [joinChannel, spaceId, channelId, channelLabel, openPanel])

    const totalUsers = stageCount + listenerCount
    const isChannelLive = totalUsers > 0

    return (
        <NavItem padding="none" onClick={onClick}>
            <NavItemContent>
                <Box centerContent shrink={false}>
                    {isUserLive ? (
                        // State 3: User is live in this channel - solid green with sound waves
                        <SoundWaveIcon size={28} />
                    ) : isChannelLive ? (
                        // State 2: Channel is live but user is not in it - green outline with pulse
                        <Box
                            centerContent
                            square="square_lg"
                            className={soundWaveStyles.liveOutlineClass}
                        >
                            <Icon type="microphone" size="square_sm" color="positive" />
                        </Box>
                    ) : (
                        // State 1: Default/general - dark background
                        <Box
                            centerContent
                            square="square_lg"
                            background="level3"
                            rounded="sm"
                            color="positive"
                        >
                            <Icon type="microphone" size="square_sm" />
                        </Box>
                    )}
                </Box>
                <Text truncate color={isUserLive ? 'default' : undefined}>
                    {channelLabel}
                </Text>
                <Box grow />
                {totalUsers > 0 && (
                    <Stack horizontal gap="sm" alignItems="center" shrink={false}>
                        {stageCount > 0 && (
                            <Stack horizontal gap="xxs" alignItems="center">
                                <Icon type="microphone" size="square_xxs" color="positive" />
                                <Text fontSize="xs" fontWeight="medium" color="positive">
                                    {formatLiveCount(stageCount)}
                                </Text>
                            </Stack>
                        )}
                        {listenerCount > 0 && (
                            <Stack horizontal gap="xxs" alignItems="center">
                                <Icon type="people" size="square_xxs" color="positive" />
                                <Text fontSize="xs" fontWeight="medium" color="positive">
                                    {formatLiveCount(listenerCount)}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                )}
            </NavItemContent>
        </NavItem>
    )
})
